import { Fragment } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { ChevronUpDownIcon } from '@heroicons/react/24/outline';

export interface SelectOption<T> {
  value: T;
  label: string;
  description?: string;
}

interface SelectProps<T> {
  value: T;
  onChange: (value: T) => void;
  options: SelectOption<T>[];
  disabled?: boolean;
  className?: string;
  placeholder?: string;
  error?: string;
  id?: string;
}

export default function Select<T extends string>({
  value,
  onChange,
  options,
  disabled = false,
  className = '',
  placeholder = 'Select an option',
  error,
  id,
}: SelectProps<T>) {
  const selectedOption = options.find((option) => option.value === value);

  return (
    <Listbox value={value} onChange={onChange} disabled={disabled}>
      {({ open }) => (
        <div className="relative">
          <Listbox.Button
            id={id}
            className={`relative w-full cursor-default rounded-md bg-white py-1.5 pl-3 pr-10 text-left text-gray-900 shadow-sm ring-1 ring-inset ${
              error ? 'ring-red-500' : 'ring-gray-300'
            } focus:outline-none focus:ring-2 focus:ring-indigo-600 sm:text-sm sm:leading-6 ${
              disabled ? 'bg-gray-50' : ''
            } ${className}`}
          >
            {selectedOption ? (
              <div>
                <span className="block truncate font-medium">
                  {selectedOption.label}
                </span>
                {selectedOption.description && (
                  <span className="block text-xs text-gray-500 truncate">
                    {selectedOption.description}
                  </span>
                )}
              </div>
            ) : (
              <span className="block truncate">{placeholder}</span>
            )}
            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
              <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
            </span>
          </Listbox.Button>

          <Transition
            show={open}
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
              {options.map((option) => (
                <Listbox.Option
                  key={option.value}
                  className={({ active }) =>
                    `relative cursor-default select-none ${option.description ? 'py-3' : 'py-2'} pl-3 pr-9 ${
                      active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                    }`
                  }
                  value={option.value}
                >
                  {({ selected, active }) => (
                    <div>
                      <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                        {option.label}
                      </span>
                      {option.description && (
                        <span className={`block text-xs mt-0.5 ${active ? 'text-indigo-200' : 'text-gray-500'}`}>
                          {option.description}
                        </span>
                      )}
                    </div>
                  )}
                </Listbox.Option>
              ))}
            </Listbox.Options>
          </Transition>
        </div>
      )}
    </Listbox>
  );
}
